/* eslint-disable react/jsx-key */
import 'react-photo-view/dist/react-photo-view.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftCircle, ArrowRightCircle, Download, ZoomIn } from 'react-feather';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { OverlayRenderProps } from 'react-photo-view/dist/types';

import { useTranslation } from 'react-i18next';

import { getVideoPosterUrl } from '@/utils/mediaUtils';

import styles from './Carousel.module.css';
import { Link } from '../link';
import { Spinner } from '../spinner';
import { Typography } from '../typography';
import { VideoPreview } from '../videoPlayer';

function getFileType(url: string) {
  const fileExtension = (url.split('.') as TypeAny).pop().toLowerCase();
  const videoExtensions = ['mp4', 'avi', 'mov', 'webm'];

  if (videoExtensions.includes(fileExtension)) return 'video';

  return 'img';
}

function slideshowOverlay(overlay: OverlayRenderProps, t: TypeAny) {
  if (!overlay.images[overlay.index].src) return null;

  const overlayFile = overlay.images[overlay.index].src as string;

  const overlayFileType = getFileType(overlayFile);

  return (
    <>
      {overlayFileType === 'video' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={`carousel-overlay-${overlay.index}`}
          className={styles.carouselOverlayVideo}
          controls
          playsInline
          poster={getVideoPosterUrl(overlayFile)}
        >
          <source src={overlayFile} type="video/mp4" />
        </video>
      )}

      <div className={styles.carouselOverlayDownload}>
        <Link href={overlayFile} theme="cta" target="_blank">
          <span className={styles.carouselOverlayDownloadLink}>
            {t('carousel.download')}
            <Download size={16} />
          </span>
        </Link>
      </div>
    </>
  );
}

interface CarouselProps {
  content: DraftMedia[];
  isDummy?: boolean;
}

export function Carousel({ content, isDummy = false }: CarouselProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadingStates, setLoadingStates] = useState<Map<number, boolean>>(new Map());
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRefs = useRef<Map<number, HTMLImageElement>>(new Map());

  const setLoadingState = useCallback((index: number, isLoading: boolean) => {
    setLoadingStates((prev) => {
      // Only update if the value would actually change
      const currentValue = prev.get(index) ?? false;
      if (currentValue === isLoading) {
        return prev; // No change needed
      }
      const next = new Map(prev);
      // Always set the value explicitly - don't delete entries
      // This prevents reverting to default when entry is missing
      next.set(index, isLoading);
      return next;
    });
  }, []);

  // Memoize content URLs to prevent unnecessary re-renders
  const contentUrls = useMemo(() => content?.map((item) => item.url) || [], [content]);

  // Check if selected image is already loaded when selection changes
  useEffect(() => {
    if (!content?.length) return;
    const selectedItem = content[selectedIndex];
    if (!selectedItem) return;

    const fileType = getFileType(selectedItem.url);
    if (fileType === 'img') {
      const imgElement = imageRefs.current.get(selectedIndex);
      if (imgElement) {
        // More reliable check: naturalWidth > 0 means image has loaded
        const isLoaded = imgElement.complete && imgElement.naturalWidth > 0;
        const currentLoadingState = loadingStates.get(selectedIndex) ?? false;

        // Only update if state would change
        if (isLoaded && currentLoadingState !== false) {
          setLoadingState(selectedIndex, false);
        } else if (!isLoaded && !imgElement.complete && currentLoadingState !== true) {
          // Image is not complete - assume it's loading
          setLoadingState(selectedIndex, true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, contentUrls.join(',')]);

  if (!content?.length) return null;

  function switchIndex(direction: string) {
    let newIndex = selectedIndex;
    // Pause current video before switching (if it exists)
    // With conditional rendering, the VideoJsPlayer will unmount anyway,
    // but pausing first ensures clean state
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (error) {
        // Handle any errors during pause operation
        // Video might already be unmounting, so errors are expected
        console.warn('Video pause failed during carousel switch:', error);
      }
    }

    if (direction === 'left') {
      newIndex -= 1;

      if (newIndex === -1) {
        newIndex = content.length - 1;
      }
    } else {
      newIndex += 1;

      if (newIndex + 1 > content.length) {
        newIndex = 0;
      }
    }

    setSelectedIndex(newIndex);
  }

  const visibleChange = (visible: boolean) => {
    if (videoRef.current && !visible) {
      try {
        videoRef.current.pause();
      } catch (error) {
        // Handle any errors during pause operation
        console.warn('Video pause failed during visibility change:', error);
      }
    }
  };

  return (
    <div data-testid="carousel" className={styles.carousel}>
      <PhotoProvider
        onVisibleChange={visibleChange}
        overlayRender={(overlay) => slideshowOverlay(overlay, t)}
      >
        {content.map((item, i) => {
          const FileElement = getFileType(item.url);

          const elementWrapperProps = {
            key: `carousel-file-${item.cloudinary_id || item.url}`,
            className: styles.carouselFile
          };

          const elementProps = {
            'data-selected': selectedIndex === i,
            className: styles.carouselSrc
          };

          if (FileElement === 'img') {
            // Default to false (not loading) - only show spinner when explicitly loading
            const isLoading = loadingStates.get(i) ?? false;

            return (
              <div {...elementWrapperProps}>
                <PhotoView src={item.url}>
                  <FileElement
                    {...elementProps}
                    loading="lazy"
                    alt={item.filename}
                    src={item.url}
                    ref={(el: HTMLImageElement | null) => {
                      if (el) {
                        imageRefs.current.set(i, el);
                        // Check if image is already loaded when ref is set
                        // Use naturalWidth > 0 for more reliable detection
                        if (el.complete && el.naturalWidth > 0) {
                          setLoadingState(i, false);
                        } else if (!el.complete) {
                          // Image hasn't loaded yet - set to loading
                          setLoadingState(i, true);
                        }
                      } else {
                        imageRefs.current.delete(i);
                      }
                    }}
                    onLoadStart={() => {
                      // Loading has started - set to true
                      setLoadingState(i, true);
                    }}
                    onLoad={(e) => {
                      // Image has finished loading - check naturalWidth for reliability
                      const img = e.currentTarget;
                      if (img.naturalWidth > 0) {
                        setLoadingState(i, false);
                      }
                    }}
                    onError={() => setLoadingState(i, false)}
                  />
                </PhotoView>

                {isLoading && selectedIndex === i && (
                  <div className={styles.carouselSpinner}>
                    <Spinner />
                  </div>
                )}

                {!isDummy && selectedIndex === i && (
                  <>
                    {item.is_raw && (
                      <span className={styles.carouselRaw}>
                        <Typography sizes={['label', 'bold']}>{t('carousel.raw')}</Typography>
                      </span>
                    )}

                    <span className={styles.carouselView} data-testid="carousel-view-icon">
                      <ZoomIn size={16} />
                    </span>
                  </>
                )}
              </div>
            );
          }

          // Default to false (not loading) - only show spinner when explicitly loading
          const isLoading = loadingStates.get(i) ?? false;
          const isSelected = selectedIndex === i;

          return (
            <div {...elementWrapperProps}>
              <PhotoView src={item.url}>
                <div {...elementProps}>
                  {isSelected ? (
                    <VideoPreview
                      src={item.url}
                      poster={getVideoPosterUrl(item.url)}
                      ref={isSelected ? videoRef : undefined}
                      data-testid={`carouselVideo-${i}`}
                      onLoadingStateChange={(isVideoLoading: boolean) =>
                        setLoadingState(i, isVideoLoading)
                      }
                    />
                  ) : (
                    <div
                      data-testid={`carouselVideo-${i}`}
                      style={{
                        height: '100%',
                        width: '100%',
                        backgroundColor: '#000'
                      }}
                    />
                  )}
                </div>
              </PhotoView>

              {isLoading && selectedIndex === i && (
                <div className={styles.carouselSpinner}>
                  <Spinner />
                </div>
              )}

              {!isDummy && selectedIndex === i && (
                <>
                  {item.is_raw && (
                    <span className={styles.carouselRaw}>
                      <Typography sizes={['label', 'bold']}>{t('carousel.raw')}</Typography>
                    </span>
                  )}

                  <span className={styles.carouselView} data-testid="carousel-view-icon">
                    <ZoomIn size={16} />
                  </span>
                </>
              )}
            </div>
          );
        })}

        {!isDummy && content.length > 1 && (
          <div className={styles.carouselSwitch}>
            <button
              className={styles.carouselSwitchButton}
              data-testid="carouselLeft"
              onClick={() => switchIndex('left')}
              type="button"
            >
              <ArrowLeftCircle size={32} />
            </button>

            <button
              className={styles.carouselSwitchButton}
              data-testid="carouselRight"
              onClick={() => switchIndex('right')}
              type="button"
            >
              <ArrowRightCircle size={32} />
            </button>
          </div>
        )}
      </PhotoProvider>
    </div>
  );
}
